import { useState, useEffect } from 'react'
import { X, Clock, CheckCircle, AlertCircle, Loader2, ChevronLeft, ChevronRight, MessageSquare, Brain } from 'lucide-react'
import { useStore } from '../store/useStore'

// Get device ID from localStorage
function getDeviceId() {
  try {
    const profile = localStorage.getItem('p5ai_research_profile_v1')
    if (profile) {
      const parsed = JSON.parse(profile)
      return parsed.participantId || null
    }
  } catch (e) {}
  return null
}

// Format relative time
function formatRelativeTime(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export default function AiHistory({ isOpen, onClose, onLoadJob }) {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingJob, setLoadingJob] = useState(null)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 })
  
  const { addAiMessage } = useStore()
  
  // Fetch history when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchHistory(1)
    }
  }, [isOpen])
  
  const fetchHistory = async (page) => {
    const deviceId = getDeviceId()
    if (!deviceId) {
      setError('No device ID found. Try sending a message first.')
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`/api/ai-history?device=${deviceId}&page=${page}&limit=10`)
      if (!response.ok) throw new Error('Failed to fetch history')
      
      const data = await response.json()
      setJobs(data.jobs || [])
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 })
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }
  
  const loadJob = async (jobId) => {
    const deviceId = getDeviceId()
    if (!deviceId) return
    
    setLoadingJob(jobId)
    
    try {
      const response = await fetch('/api/ai-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId, deviceId })
      })
      
      if (!response.ok) throw new Error('Failed to fetch job')
      
      const job = await response.json()
      
      // Add to chat
      addAiMessage({
        role: 'user',
        content: job.prompt,
        displayContent: job.prompt
      })
      
      const responseContent = job.response || 
        (job.thinking ? '[AI was still thinking - see reasoning below]' : '[No response]')
      
      addAiMessage({
        role: 'assistant',
        content: responseContent,
        reasoning: job.thinking || '',
        recovered: true
      })
      
      onClose()
      
    } catch (e) {
      setError(e.message)
    } finally {
      setLoadingJob(null)
    }
  }
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle size={14} className="text-green-400" />
      case 'streaming':
        return <Loader2 size={14} className="text-blue-400 animate-spin" />
      case 'error':
        return <AlertCircle size={14} className="text-red-400" />
      default:
        return <Clock size={14} className="text-gray-400" />
    }
  }
  
  const getProviderBadge = (provider) => {
    const colors = {
      openai: 'bg-green-900 text-green-300',
      anthropic: 'bg-orange-900 text-orange-300',
      gemini: 'bg-blue-900 text-blue-300'
    }
    return colors[provider] || 'bg-gray-700 text-gray-300'
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-p5-pink" />
            <h2 className="text-lg font-semibold">Chat History</h2>
            {pagination.total > 0 && (
              <span className="text-sm text-gray-400">({pagination.total} conversations)</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-800 rounded"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-p5-pink" />
            </div>
          )}
          
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}
          
          {!loading && !error && jobs.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
              <p>No chat history yet</p>
              <p className="text-sm">Start a conversation with the AI!</p>
            </div>
          )}
          
          {jobs.map((job) => (
            <button
              key={job.id}
              onClick={() => loadJob(job.id)}
              disabled={loadingJob === job.id}
              className="w-full text-left p-3 bg-gray-800 hover:bg-gray-750 rounded-lg transition-colors border border-gray-700 hover:border-gray-600 disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  {loadingJob === job.id ? (
                    <Loader2 size={14} className="animate-spin text-p5-pink" />
                  ) : (
                    getStatusIcon(job.status)
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${getProviderBadge(job.provider)}`}>
                      {job.provider}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatRelativeTime(job.created_at)}
                    </span>
                    {job.response_length > 0 && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <MessageSquare size={10} />
                        {(job.response_length / 1000).toFixed(1)}k
                      </span>
                    )}
                    {job.thinking_length > 0 && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Brain size={10} />
                        {(job.thinking_length / 1000).toFixed(1)}k
                      </span>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-200 line-clamp-2">
                    {job.prompt_preview}
                    {job.prompt_preview?.length >= 200 && '...'}
                  </p>
                  
                  {job.error_message && (
                    <p className="text-xs text-red-400 mt-1 truncate">
                      Error: {job.error_message}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-gray-700">
            <button
              onClick={() => fetchHistory(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            
            <span className="text-sm text-gray-400">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            
            <button
              onClick={() => fetchHistory(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
